import React, { useState, useContext, useEffect } from "react"
import PropTypes from "prop-types"
import { makeStyles } from "@material-ui/core/styles"
import AssignmentIcon from "@material-ui/icons/Assignment"
import { green, red } from "@material-ui/core/colors"
import {
  Button,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  Input,
  MenuItem,
} from "@material-ui/core"
import { requestPostPurchByQuarter } from "../api/apiCalls"
import { UserContext } from "../contexts/UserContext"
import { HistoryContext } from "../contexts/HistoryContext"
import { MessagingContext } from "../contexts/MessagingContext"
import ConfirmationModal from "./modals/ConfirmationModal"
import InputLabel from "@material-ui/core/InputLabel"
import { format, addMonths } from "date-fns"

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 20,
  },
  card: {
    "& > *": {
      margin: theme.spacing(1),
    },
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    spacing: 3,
    marginBottom: 50,
  },
  error: {
    color: red[500],
  },
  successMessage: {
    color: green[500],
    fontSize: "0.8em",
    marginTop: 10,
    maxWidth: 300,
  },
  input: {
    display: "none",
  },
  detail: {
    fontSize: 12,
    fontStyle: "italic",
  },
  uploadBtn: {
    position: "absolute",
    right: 10,
    top: 10,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 300,
  },
  formControlSM: {
    marginTop: "20px",
    minWidth: 120,
  },
  button: {
    marginTop: 20,
    minWidth: 300,
    color: "white",
    maxWidth: 300,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  buttonDisabled: {
    color: "rgba(0,0,0,0.2)",
  },
  done: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  buttonProgressValue: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -9,
    marginLeft: -7,
  },
  wrapper: {
    position: "relative",
  },
  quarterRadioLabel: {
    marginTop: 10,
  },
  successMessageHolder: {
    minHeight: 50,
  },
  lambdaButton: {
    marginTop: 20,
    minWidth: 300,
    color: "white",
    maxWidth: 300,
    backgroundColor: "#DD5639",
    borderColor: "#DD5639",
    "&:hover": {
      backgroundColor: "#9F361F",
      borderColor: "#0062cc",
      boxShadow: "none",
    },
    "&:active": {
      boxShadow: "none",
      backgroundColor: "#9F361F",
      borderColor: "#0062cc",
    },
    "&:focus": {
      boxShadow: "none",
      outline: "none !important",
    },
  },
}))

const QuarterlyUploadFileButton = ({ label, callBack, maxQuarters }) => {
  const [transferring, setTransferring] = useState(false)
  const [quarter, setQuarter] = useState(null)
  const [quarterNumber, setQuarterNumber] = useState(null)
  const [year, setYear] = useState("")
  const [pivot, setPivot] = useState("PurchPost")
  const [queryValues, setQueryValues] = useState({
    postRangeStartQtr: "",
    postRangeStartYr: "",
    postRangeEndQtr: "",
    postRangeEndYr: "",
    purchRangeStartQtr: "",
    purchRangeStartYr: "",
    purchRangeEndQtr: "",
    purchRangeEndYr: "",
  })
  const [selectionLabel, setSelectionLabel] = useState("")
  const [modalOpen, setModalOpen] = useState(false)
  const [error, setError] = useState()
  const classes = useStyles()
  const { user } = useContext(UserContext)
  const { fetchHistory } = useContext(HistoryContext)
  const { addErrorMessage, addSuccessMessage } = useContext(MessagingContext)

  const range = (start, stop, step) =>
    Array.from({ length: (stop - start) / step + 1 }, (_, i) =>
      (start + i * step).toString()
    )
  const currentYear = format(new Date(), "yyyy")

  const availableQuarters = ["Q1", "Q2", "Q3", "Q4"]
  const availablePostYears = range(2013, currentYear, 1)
  const availablePurchYears = range(
    2020,
    format(addMonths(new Date(), 18), "yyyy"),
    1
  )

  useEffect(() => {
    const {
      postRangeStartQtr,
      postRangeStartYr,
      postRangeEndQtr,
      postRangeEndYr,
      purchRangeStartQtr,
      purchRangeStartYr,
      purchRangeEndQtr,
      purchRangeEndYr,
    } = queryValues

    let returnStr = ""

    if (pivot === "Post") {
      returnStr = `Post_${postRangeStartQtr}-${postRangeStartYr}-${postRangeEndQtr}-${postRangeEndYr}`
    }

    if (pivot === "Purch") {
      returnStr = `Purch_${purchRangeStartQtr}-${purchRangeStartYr}-${purchRangeEndQtr}-${purchRangeEndYr}`
    }

    if (pivot === "PostPurch") {
      returnStr = `Purch_${purchRangeStartQtr}-${purchRangeStartYr}-${purchRangeEndQtr}-${purchRangeEndYr}_Post_${postRangeStartQtr}-${postRangeStartYr}-${postRangeEndQtr}-${postRangeEndYr}`
    }
    setSelectionLabel(returnStr)
  }, [queryValues])

  const handleQueryValueChange = (event) => {
    setQueryValues({ ...queryValues, [event.target.name]: event.target.value })
  }

  const handlePivotChange = (event) => {
    setPivot(event.target.value)
    setQueryValues({})
  }

  const launchConfirmation = () => {
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
  }

  const getPostStartYr = () => {
    return availablePostYears
  }

  const getPostEndYr = () => {
    return availablePostYears
  }

  const getPurchStartYr = () => {
    if (pivot === "PurchPost" && queryValues && queryValues.postedRangeEndYr) {
      return range(
        queryValues.postedRangeEndQtr === "Q4"
          ? Number(queryValues.postedRangeEndYr) + 1
          : Number(queryValues.postedRangeEndYr),
        format(addMonths(new Date(), 18), "yyyy"),
        1
      )
    }
    return availablePurchYears
  }

  const getPurchEndYr = () => {
    if (pivot === "PurchPost" && queryValues && queryValues.postedRangeEndYr) {
      return range(
        Number(queryValues.postedRangeEndYr),
        format(addMonths(new Date(), 18), "yyyy"),
        1
      )
    }
    return availablePurchYears
  }

  const verifyPurch =
    queryValues.purchRangeStartQtr &&
    queryValues.purchRangeStartYr &&
    queryValues.purchRangeEndQtr &&
    queryValues.purchRangeEndYr
  const verifyPost =
    queryValues.postedRangeStartQtr &&
    queryValues.postedRangeStartYr &&
    queryValues.postedRangeEndQtr &&
    queryValues.postedRangeEndYr

  const callPostPurch = () => {
    setTransferring(true)
    // send queryString to woodstock
    let returnReportName = ""

    if (pivot === "Purch") {
      returnReportName = `Purch_${queryValues.purchRangeStartQtr}-${queryValues.purchRangeStartYr}-${queryValues.purchRangeEndQtr}-${queryValues.purchRangeEndYr}`
    }

    if (pivot === "Post") {
      returnReportName = `Post_${queryValues.postedRangeStartQtr}-${queryValues.postedRangeStartYr}-${queryValues.postedRangeEndQtr}-${queryValues.postedRangeEndYr}`
    }

    if (pivot === "PurchPost") {
      returnReportName = `Post_${queryValues.postedRangeStartQtr}-${queryValues.postedRangeStartYr}-${queryValues.postedRangeEndQtr}-${queryValues.postedRangeEndYr}_Purch_${queryValues.purchRangeStartQtr}-${queryValues.purchRangeStartYr}-${queryValues.purchRangeEndQtr}-${queryValues.purchRangeEndYr}`
    }

    let sendObj = {
      ReportType: pivot || "",
      PurchReportParam:
        pivot === "Purch" || pivot === "PurchPost"
          ? `${queryValues.purchRangeStartQtr}-${queryValues.purchRangeStartYr}:${queryValues.purchRangeEndQtr}-${queryValues.purchRangeEndYr}`
          : "",
      PostReportParam:
        pivot === "Post" || pivot === "PurchPost"
          ? `${queryValues.postedRangeStartQtr}-${queryValues.postedRangeStartYr}:${queryValues.postedRangeEndQtr}-${queryValues.postedRangeEndYr}`
          : "",
      ReportName: returnReportName,
    }
    requestPostPurchByQuarter(user, sendObj)
      .then((data) => {
        if (data.status === 200) {
          addSuccessMessage(
            "Request",
            `for Post/Purch Report (${selectionLabel}) generated.`
          )
          setTransferring(false)
          setYear("")
          setQuarter("")
          setQuarterNumber("")
          fetchHistory(user)
        } else {
          addErrorMessage(
            "Error",
            `Unable to process Post/Purch Report (${selectionLabel}).`
          )
        }
        if (callBack) {
          callBack()
        }
      })
      .catch((error) => {
        setError(error)
      })
  }

  const generateVerificationMessage = () => {
    if (pivot === "PurchPost") {
      return `Request Purch/Post Report for Posted Range: ${queryValues.postedRangeStartQtr}/${queryValues.postedRangeStartYr} to ${queryValues.postedRangeEndQtr}/${queryValues.postedRangeEndYr} AND Purchased Range: ${queryValues.purchRangeStartQtr}/${queryValues.purchRangeStartYr} to ${queryValues.purchRangeEndQtr}/${queryValues.purchRangeEndYr}`
    }

    if (pivot === "Purch") {
      return `Request Purch/Post Report for Purchased Range: ${queryValues.purchRangeStartQtr}/${queryValues.purchRangeStartYr} to ${queryValues.purchRangeEndQtr}/${queryValues.purchRangeEndYr}`
    }

    if (pivot === "Post") {
      return `Request Purch/Post Report for Posted Range: ${queryValues.postedRangeStartQtr}/${queryValues.postedRangeStartYr} to ${queryValues.postedRangeEndQtr}/${queryValues.postedRangeEndYr}`
    }
  }

  console.log(" queryValues ", queryValues)

  return (
    <div className={classes.root}>
      <ConfirmationModal
        content={generateVerificationMessage()}
        openModal={modalOpen}
        confirmFunction={callPostPurch}
        closeModalFunction={closeModal}
      />
      <FormControl className={classes.formControl}>
        <div>
          <FormLabel style={{ fontWeight: "bold" }}>Report Data: </FormLabel>
          <Select
            labelId="pivot-type"
            id="pivot-type"
            value={pivot}
            onChange={handlePivotChange}
            input={<Input />}
            style={{ marginLeft: "80px", marginRight: "15px" }}
          >
            <MenuItem value="PurchPost">Post/Purch</MenuItem>
            <MenuItem value="Purch">Purch Only</MenuItem>
            <MenuItem value="Post">Post Only</MenuItem>
          </Select>
        </div>

        {/*POSTED RANGE START*/}
        {(pivot === "Post" || pivot === "PurchPost") && (
          <div>
            <div style={{ marginTop: "40px", fontWeight: "bold" }}>
              Posted Range:{" "}
            </div>
            <div style={{ marginTop: "-20px" }}>
              <FormControl className={classes.formControlSM}>
                <InputLabel id="demo-simple-select-error-label">
                  Starting Qtr
                </InputLabel>
                <Select
                  name="postedRangeStartQtr"
                  value={queryValues.postedRangeStartQtr || ""}
                  onChange={handleQueryValueChange}
                  input={<Input />}
                  style={{ marginRight: "15px" }}
                >
                  {availableQuarters.map((qtr) => (
                    <MenuItem key={qtr} value={qtr}>
                      {qtr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={classes.formControlSM}>
                <InputLabel id="demo-simple-select-error-label">
                  Starting Yr
                </InputLabel>
                <Select
                  name="postedRangeStartYr"
                  value={queryValues.postedRangeStartYr || ""}
                  onChange={handleQueryValueChange}
                  input={<Input />}
                  style={{ marginRight: "15px" }}
                >
                  {getPostStartYr().map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                className={classes.formControlSM}
                style={{ minWidth: "25px" }}
              >
                <InputLabel> to </InputLabel>
              </FormControl>
              <FormControl className={classes.formControlSM}>
                <InputLabel id="demo-simple-select-error-label">
                  Ending Qtr
                </InputLabel>
                <Select
                  name="postedRangeEndQtr"
                  value={queryValues.postedRangeEndQtr || ""}
                  onChange={handleQueryValueChange}
                  input={<Input />}
                  style={{ marginRight: "15px" }}
                >
                  {availableQuarters.map((qtr) => (
                    <MenuItem key={qtr} value={qtr}>
                      {qtr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={classes.formControlSM}>
                <InputLabel id="demo-simple-select-error-label">
                  Ending Yr
                </InputLabel>
                <Select
                  name="postedRangeEndYr"
                  value={queryValues.postedRangeEndYr || ""}
                  onChange={handleQueryValueChange}
                  input={<Input />}
                  style={{ marginRight: "15px" }}
                >
                  {getPostEndYr().map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        )}

        {/*PURCHASED RANGE START*/}
        {(pivot === "Purch" || (pivot === "PurchPost" && verifyPost)) && (
          <div>
            <div style={{ marginTop: "40px", fontWeight: "bold" }}>
              Purchased Range:{" "}
            </div>
            <div style={{ marginTop: "-20px" }}>
              <FormControl className={classes.formControlSM}>
                <InputLabel id="demo-simple-select-error-label">
                  Starting Qtr
                </InputLabel>
                <Select
                  name="purchRangeStartQtr"
                  value={queryValues.purchRangeStartQtr || ""}
                  onChange={handleQueryValueChange}
                  input={<Input />}
                  style={{ marginRight: "15px" }}
                >
                  {availableQuarters.map((qtr) => (
                    <MenuItem key={qtr} value={qtr}>
                      {qtr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={classes.formControlSM}>
                <InputLabel id="demo-simple-select-error-label">
                  Starting Yr
                </InputLabel>
                <Select
                  name="purchRangeStartYr"
                  value={queryValues.purchRangeStartYr || ""}
                  onChange={handleQueryValueChange}
                  input={<Input />}
                  style={{ marginRight: "15px" }}
                >
                  {getPurchStartYr().map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                className={classes.formControlSM}
                style={{ minWidth: "25px" }}
              >
                <InputLabel> to </InputLabel>
              </FormControl>
              <FormControl className={classes.formControlSM}>
                <InputLabel id="demo-simple-select-error-label">
                  Ending Qtr
                </InputLabel>
                <Select
                  name="purchRangeEndQtr"
                  value={queryValues.purchRangeEndQtr || ""}
                  onChange={handleQueryValueChange}
                  input={<Input />}
                  style={{ marginRight: "15px" }}
                >
                  {availableQuarters.map((qtr) => (
                    <MenuItem key={qtr} value={qtr}>
                      {qtr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={classes.formControlSM}>
                <InputLabel id="demo-simple-select-error-label">
                  Ending Yr
                </InputLabel>
                <Select
                  name="purchRangeEndYr"
                  value={queryValues.purchRangeEndYr || ""}
                  onChange={handleQueryValueChange}
                  input={<Input />}
                  style={{ marginRight: "15px" }}
                >
                  {getPurchEndYr().map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        )}

        <p style={{ margin: "25px" }}></p>

        {(pivot === "PurchPost" || pivot === "Post") && verifyPost && (
          <div style={{ fontWeight: "bold" }}>
            Posted Range: {queryValues.postedRangeStartQtr}/
            {queryValues.postedRangeStartYr} to {queryValues.postedRangeEndQtr}/
            {queryValues.postedRangeEndYr}
          </div>
        )}

        {(pivot === "PurchPost" || pivot === "Purch") && verifyPurch && (
          <div style={{ fontWeight: "bold" }}>
            Purchased Range: {queryValues.purchRangeStartQtr}/
            {queryValues.purchRangeStartYr} to {queryValues.purchRangeEndQtr}/
            {queryValues.purchRangeEndYr}
          </div>
        )}

        <div style={{ textAlign: "right", marginTop: "30px" }}>
          <Button
            className={classes.lambdaButton}
            style={{ opacity: transferring ? "0.2" : "1.0" }}
            variant={transferring ? "outlined" : "contained"}
            color="secondary"
            disableElevation
            disabled={
              (!(pivot === "PurchPost" && verifyPost && verifyPurch) &&
                !(pivot === "Purch" && verifyPurch) &&
                !(pivot === "Post" && verifyPost)) ||
              transferring
            }
            onClick={launchConfirmation}
            startIcon={<AssignmentIcon />}
          >
            DOWNLOAD
          </Button>
          {error && <div className={classes.error}>{error}</div>}
        </div>
        {/*<FormLabel>{label} - Starting Yr</FormLabel>*/}
        {/*<Select*/}
        {/*  labelId="id-start-year"*/}
        {/*  id="select-start-year"*/}
        {/*  value={year}*/}
        {/*  onChange={handleQueryValueChange}*/}
        {/*  input={<Input />}*/}
        {/*>*/}
        {/*  {availableYears.map((year) => (*/}
        {/*    <MenuItem key={year} value={year}>*/}
        {/*      {year}*/}
        {/*    </MenuItem>*/}
        {/*  ))}*/}
        {/*</Select>*/}
        {/*<FormLabel className={classes.quarterRadioLabel}>Starting Quarter</FormLabel>*/}
        {/*<RadioGroup*/}
        {/*  aria-label="quarter"*/}
        {/*  row*/}
        {/*  name="quarter"*/}
        {/*  value={quarter}*/}
        {/*  onChange={handleQuarterChange}*/}
        {/*>*/}
        {/*  {availableQuarters.map((quarter) => (*/}
        {/*    <FormControlLabel*/}
        {/*      key={quarter}*/}
        {/*      value={quarter}*/}
        {/*      control={<Radio color="primary" />}*/}
        {/*      label={quarter}*/}
        {/*      labelPlacement="start"*/}
        {/*    />*/}
        {/*  ))}*/}
        {/*</RadioGroup>*/}
        {/*<FormLabel className={classes.quarterRadioLabel}>*/}
        {/*  Number of Consecutive Quarters*/}
        {/*</FormLabel>*/}
        {/*<RadioGroup*/}
        {/*  aria-label="quarter"*/}
        {/*  row*/}
        {/*  name="quarterNumber"*/}
        {/*  value={quarterNumber}*/}
        {/*  onChange={handleQuarterNumberChange}*/}
        {/*>*/}
        {/*  {createQuarterNumberOptions()}*/}
        {/*</RadioGroup>*/}
        {/*<FormLabel>Query: {selectionLabel && selectionLabel}</FormLabel>*/}
        {/*<Button*/}
        {/*  className={classes.lambdaButton}*/}
        {/*  style={{ opacity: transferring ? "0.2" : "1.0" }}*/}
        {/*  variant={transferring ? "outlined" : "contained"}*/}
        {/*  color="secondary"*/}
        {/*  disableElevation*/}
        {/*  disabled={!(year && quarter && quarterNumber) || transferring}*/}
        {/*  onClick={launchConfirmation}*/}
        {/*  startIcon={<AssignmentIcon />}*/}
        {/*>*/}
        {/*  {label}*/}
        {/*</Button>*/}
        {/*{error && <div className={classes.error}>{error}</div>}*/}
      </FormControl>
    </div>
  )
}

QuarterlyUploadFileButton.propTypes = {
  fileName: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  callBack: PropTypes.func,
  context: PropTypes.string,
  maxQuarters: PropTypes.number,
}

export default QuarterlyUploadFileButton
